import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';

const Contact = () => {
  return (
    <>
      <Helmet>
        <title>Contact Us - Tasks IT Services | Get in Touch</title>
        <meta name="description" content="Contact Tasks IT Services. We respond quickly to inquiries about services, partnerships, and support." />
      </Helmet>

      {/* Background */}
      <div className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900" />
        <div className="pointer-events-none absolute -top-40 -left-40 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="pointer-events-none absolute -bottom-40 -right-40 w-[30rem] h-[30rem] bg-blue-600/20 rounded-full blur-3xl" />

        {/* Hero */}
        <section className="relative pt-24 pb-12 text-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              Let’s Start the Conversation
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .1 }} className="text-lg md:text-xl text-blue-100 max-w-2xl mx-auto">
              Reach out about solutions, partnerships, or support. We typically respond within one business day.
            </motion.p>
          </div>
        </section>

        {/* Main Card Grid */}
        <section className="relative pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10">

            {/* Sidebar */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-5 flex flex-col gap-8"
            >
              <div className="relative h-64 w-full overflow-hidden rounded-3xl shadow-2xl">
                <img
                  src="https://www.stratospherenetworks.com/blog/wp-content/uploads/2021/06/cabling-infrastructure-stock-photo.jpg"
                  alt="Modern collaborative workspace"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-900/70 via-indigo-900/40 to-transparent" />
                <div className="absolute bottom-4 left-5 text-white">
                  <p className="font-semibold text-sm uppercase tracking-wide text-blue-200">We Deliver</p>
                  <h3 className="text-2xl font-bold">Secure Scalable Infrastructure</h3>
                </div>
              </div>

              <div className="grid gap-6">
                <div className="rounded-2xl p-6 bg-white/10 border border-white/15 backdrop-blur-xl text-white">
                  <h3 className="text-lg font-semibold mb-4">Contact Info</h3>
                  <div className="space-y-5 text-sm">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center">
                        <Mail className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium">Email</p>
                        <a href="mailto:GlennRosas@hotmail.com" className="opacity-80 hover:text-cyan-300 transition">
                          GlennRosas@hotmail.com
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center">
                        <Phone className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium">Phone</p>
                        <p className="opacity-80">+81 70-2633-2830</p>
                        <p className="opacity-60 text-xs">Mon–Fri 9:00–18:00 JST</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center">
                        <MapPin className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium">Office</p>
                        <p className="opacity-80">Machida City</p>
                        <p className="opacity-80">Tokyo, Japan</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl p-6 bg-white/10 border border-white/15 backdrop-blur-xl text-white">
                  <h3 className="text-lg font-semibold mb-4">Office Hours</h3>
                  <div className="space-y-3 text-sm">
                    {[
                      ['Mon – Fri', '09:00 – 18:00'],
                      ['Saturday', '10:00 – 14:00'],
                      ['Sunday', 'Closed']
                    ].map(([d, h]) => (
                      <div key={d} className="flex items-center justify-between">
                        <span className="opacity-80">{d}</span>
                        <span className="font-medium">{h}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 flex items-center gap-2 text-xs text-blue-100/80">
                    <Clock className="h-4 w-4" /> Response goal: &lt; 24h
                  </div>
                </div>

                <div className="rounded-2xl p-6 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border border-cyan-400/30 backdrop-blur-xl text-cyan-50">
                  <h3 className="text-lg font-semibold mb-2">Why Reach Out?</h3>
                  <ul className="space-y-2 text-sm opacity-90">
                    <li className="flex gap-2">
                      <span className="text-cyan-300">•</span> Project scoping & migration planning
                    </li>
                    <li className="flex gap-2">
                      <span className="text-cyan-300">•</span> Security & infrastructure assessments
                    </li>
                    <li className="flex gap-2">
                      <span className="text-cyan-300">•</span> AV & conferencing standardization
                    </li>
                    <li className="flex gap-2">
                      <span className="text-cyan-300">•</span> Warehousing & deployment logistics
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Contact;